import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Language } from '../../core/services/phone-service-api.model';
import { AM_TYPES, AmBookPayload } from './cc-numbers.model';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmRadioGroupComponent,
  HlmRadioComponent,
  HLM_FORM_FIELD,
  HlmSelectOption,
} from '../../ui';

/**
 * Dialog for booking a new answering machine on a phone service, or — when the
 * phone service already has a voicebox — activating the AM for one additional
 * CC number (`activateOnly`, old CMT's 'active' mode: only the voicebox type is
 * chosen and POSTed for that number). Editing an existing AM's language happens
 * inline on the CC Numbers page (EB-3856).
 */
export interface AmBookDialogData {
  languages: Language[];
  /** Activate the existing AM for `number` — only the voicebox type is asked (EB-3872). */
  activateOnly?: boolean;
  /** Current type of the existing AM — pre-selects the type in activateOnly mode (old CMT parity). */
  currentType?: string | null;
  /** Pre-selected/display number (required for activateOnly). */
  number?: string;
  /** CC numbers to choose from when booking a new AM. */
  numberOptions?: HlmSelectOption<string>[];
}

@Component({
  selector: 'cmt-am-book-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmRadioGroupComponent, HlmRadioComponent,
    ...HLM_FORM_FIELD,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucidePhoneCall" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>{{ data.activateOnly ? 'Activate Answering Machine' : 'Book Answering Machine' }}</h2>
          <p hlmDialogDescription>
            @if (data.number) {
              Number: {{ data.number }}
            } @else {
              Configure answering machine settings
            }
          </p>
        </div>
        <button hlmDialogClose tabindex="-1" (click)="dialogRef.close(null)">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="overflow-visible">
        <form [formGroup]="form" (ngSubmit)="submit()" class="flex flex-col gap-4">
          <!-- Number: choose which CC number the AM attaches to (new booking only) -->
          @if (!data.activateOnly && (data.numberOptions?.length ?? 0) > 0) {
            <hlm-form-field>
              <label hlmLabel>Number</label>
              <hlm-select
                [options]="data.numberOptions ?? []"
                [value]="form.controls.number.value"
                placeholder="Select a number…"
                (valueChange)="form.controls.number.setValue($any($event) || '')"
              />
            </hlm-form-field>
          }

          @if (!data.activateOnly) {
            <hlm-form-field>
              <label hlmLabel for="am-email">Email</label>
              <input id="am-email" hlmInput [formControl]="form.controls.email" type="email" placeholder="voicemail@example.com" />
            </hlm-form-field>

            <hlm-form-field>
              <label hlmLabel>Language</label>
              <hlm-select
                [options]="languageOptions"
                [value]="form.controls.language.value"
                (valueChange)="form.controls.language.setValue($any($event) || '')"
              />
            </hlm-form-field>
          }

          <hlm-form-field>
            <label hlmLabel>Voicebox Type</label>
            <hlm-select
              [options]="amTypeOptions"
              [value]="form.controls.type.value"
              (valueChange)="form.controls.type.setValue($any($event) || defaultAmType)"
            />
            <p class="mt-1.5 flex items-start gap-1.5 text-sm text-muted-foreground">
              <hlm-icon name="lucideInfo" size="0.85rem" class="mt-0.5 flex-none" />
              This is the initial type — it can be changed later under
              Notifications &amp; Forwardings (Direct Forwardings of this number).
            </p>
          </hlm-form-field>

          @if (!data.activateOnly) {
            <div>
              <p class="font-mono-feat mb-2 text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Number Type</p>
              <hlm-radio-group [value]="form.controls.withDummyNumber.value" (valueChange)="form.controls.withDummyNumber.setValue($any($event))">
                <hlm-radio [value]="false">Public Number</hlm-radio>
                <hlm-radio [value]="true">Dummy Number</hlm-radio>
              </hlm-radio-group>
            </div>
          }
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" type="button" (click)="dialogRef.close(null)">Cancel</button>
        <button hlmBtn type="button" (click)="submit()" [disabled]="form.invalid">
          <hlm-icon name="lucideSave" size="0.9rem" /> {{ data.activateOnly ? 'Activate' : 'Book' }}
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class AmBookDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AmBookDialogComponent>);
  readonly data: AmBookDialogData = inject(MAT_DIALOG_DATA);
  readonly amTypes = AM_TYPES;
  /** Default voicebox type — the first AM type ("AB-nicht erreichbar"). */
  readonly defaultAmType: string = AM_TYPES[0].value;

  readonly languageOptions: HlmSelectOption<string>[] = this.data.languages.map(l => ({
    value: l.abbreviation, label: l.name,
  }));
  readonly amTypeOptions: HlmSelectOption<string>[] = AM_TYPES.map(t => ({
    value: t.value, label: t.label,
  }));

  readonly form: FormGroup<{
    number: FormControl<string>;
    email: FormControl<string>;
    language: FormControl<string>;
    type: FormControl<string>;
    withDummyNumber: FormControl<boolean>;
  }>;

  constructor() {
    const activateOnly = this.data.activateOnly === true;
    this.form = new FormGroup({
      number: new FormControl(this.data.number ?? (this.data.numberOptions?.[0]?.value ?? ''), {
        nonNullable: true,
        validators: [Validators.required],
      }),
      email: new FormControl('', {
        nonNullable: true,
        validators: activateOnly ? [] : [Validators.required, Validators.email],
      }),
      language: new FormControl('', {
        nonNullable: true,
        validators: activateOnly ? [] : [Validators.required],
      }),
      // Pre-select the existing AM's type when activating (old CMT parity).
      type: new FormControl(this.data.currentType || this.defaultAmType, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      withDummyNumber: new FormControl(false, { nonNullable: true }),
    });
  }

  submit(): void {
    if (this.form.invalid) return;
    const val = this.form.getRawValue();
    // activateOnly POSTs just the voicebox type for the given number (old CMT 'active' mode)
    const result: AmBookPayload = this.data.activateOnly
      ? { type: val.type, number: val.number }
      : {
          email: val.email,
          language: val.language,
          type: val.type,
          withDummyNumber: val.withDummyNumber,
          number: val.number,
        };
    this.dialogRef.close(result);
  }
}
